/**
 * Kurashi Wishlist Engine - Zero-App Native Gallery System
 * Persistent via localStorage, 0 external dependencies.
 */
(function() {
  const STORAGE_KEY = 'kurashi_wishlist_v1';

  class KurashiWishlistManager {
    constructor() {
      this.strings = JSON.parse(document.getElementById('kr-wishlist-strings').textContent);
      this.items = this.loadItems();
      this.init();
    }

    text(key, title = '') {
      return this.strings[key].replace('[title]', title);
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
    }

    loadItems() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const items = raw ? JSON.parse(raw) : [];
        return Array.isArray(items) ? items.filter(item => item && /^\d+$/.test(String(item.id))) : [];
      } catch (e) {
        console.warn('[Kurashi Wishlist] Failed to read localStorage', e);
        return [];
      }
    }

    saveItems() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.warn('[Kurashi Wishlist] Failed to save to localStorage', e);
      }
      this.updateUI();
    }

    getItems() {
      return this.items;
    }

    hasItem(id) {
      return this.items.some(item => String(item.id) === String(id));
    }

    toggleItem(itemData) {
      const index = this.items.findIndex(item => String(item.id) === String(itemData.id));
      let added = false;
      if (index > -1) {
        this.items.splice(index, 1);
        added = false;
        this.showToast(this.text('removed', itemData.title || this.text('work')));
      } else {
        this.items.unshift({
          id: String(itemData.id),
          handle: itemData.handle || '',
          title: itemData.title || '',
          price: itemData.price || '',
          image: itemData.image || '',
          url: itemData.url || '',
          addedAt: Date.now()
        });
        added = true;
        this.showToast(this.text('added', itemData.title || this.text('work')));
      }
      this.saveItems();
      return added;
    }

    removeItem(id) {
      const drawer = document.getElementById('KurashiWishlistDrawer');
      const hadFocus = drawer?.contains(document.activeElement);
      const prevLen = this.items.length;
      this.items = this.items.filter(item => String(item.id) !== String(id));
      if (this.items.length !== prevLen) {
        this.saveItems();
        this.showToast(this.text('removed_generic'));
        if (hadFocus && !this.isFocusable(document.activeElement)) drawer.querySelector('.kr-wishlist-drawer__close').focus();
      }
    }

    toggleFromButton(btn) {
      if (!btn) return;
      const pid = btn.dataset.productId;
      if (!pid || !/^\d+$/.test(pid)) return;

      // When already in wishlist, clicking directly opens Entrance 1 (the drawer)
      if (this.hasItem(pid)) {
        this.openDrawer();
        return;
      }

      const itemData = {
        id: pid,
        handle: btn.dataset.productHandle,
        title: btn.dataset.productTitle,
        price: btn.dataset.productPrice,
        image: btn.dataset.productImage,
        url: btn.dataset.productUrl
      };
      this.toggleItem(itemData);
    }

    updateUI() {
      this.updateBadges();
      this.updateButtons();
      this.renderDrawer();
    }

    updateBadges() {
      const count = this.items.length;
      document.querySelectorAll('#kr-wishlist-count-bubble, .kr-wishlist-count-bubble').forEach(bubble => {
        const textSpan = bubble.querySelector('span');
        if (textSpan) textSpan.textContent = count;
        if (count > 0) {
          bubble.classList.remove('hidden');
          bubble.classList.add('kr-badge-bounce');
          setTimeout(() => bubble.classList.remove('kr-badge-bounce'), 350);
        } else {
          bubble.classList.add('hidden');
        }
      });
    }

    updateButtons() {
      document.querySelectorAll('.kr-wishlist-pdp-btn').forEach(btn => {
        const pid = btn.dataset.productId;
        const textSpan = btn.querySelector('.kr-wishlist-btn-text');
        if (this.hasItem(pid)) {
          btn.classList.add('is-active');
          if (textSpan) textSpan.textContent = this.text('saved');
          btn.setAttribute('aria-label', this.text('saved'));
        } else {
          btn.classList.remove('is-active');
          if (textSpan) textSpan.textContent = this.text('add');
          btn.setAttribute('aria-label', this.text('add'));
        }
      });
    }

    isFocusable(element) {
      if (!element?.isConnected || element.disabled || element.closest('[hidden], [inert]')) return false;
      for (let node = element; node && node !== document; node = node.parentElement) {
        const style = window.getComputedStyle ? window.getComputedStyle(node) : node.style;
        if (style?.display === 'none' || style?.visibility === 'hidden') return false;
      }
      return true;
    }

    focusTrigger(preferred) {
      const fallback = [...document.querySelectorAll('[aria-controls="KurashiWishlistDrawer"]')].find(el => this.isFocusable(el));
      (this.isFocusable(preferred) ? preferred : fallback)?.focus();
    }

    cancelDrawerFocus() {
      this.pendingDrawerFocus?.();
      this.pendingDrawerFocus = null;
    }

    focusVisibleDrawer(drawer) {
      this.cancelDrawerFocus();
      const target = drawer.querySelector('.kr-wishlist-drawer__close');
      // DOM-only consumers have no rendering/animation lifecycle.
      if (!window.requestAnimationFrame) {
        if (this.isFocusable(target)) target.focus();
        return;
      }
      let frame, timer;
      const cleanup = () => {
        window.cancelAnimationFrame(frame);
        clearTimeout(timer);
        drawer.removeEventListener('transitionend', attempt);
        if (this.pendingDrawerFocus === cleanup) this.pendingDrawerFocus = null;
      };
      const attempt = () => {
        if (!drawer.classList.contains('is-open') || drawer.hasAttribute('inert')) { cleanup(); return; }
        // A user who already entered the dialog owns their chosen focus.
        if (drawer.contains(document.activeElement)) { cleanup(); return; }
        if (this.isFocusable(target)) { target.focus(); cleanup(); }
      };
      const tick = () => {
        attempt();
        if (this.pendingDrawerFocus === cleanup) frame = window.requestAnimationFrame(tick);
      };
      this.pendingDrawerFocus = cleanup;
      drawer.addEventListener('transitionend', attempt);
      frame = window.requestAnimationFrame(tick);
      // Bound the frame loop even without transitionend (including reduced motion).
      timer = setTimeout(() => { attempt(); cleanup(); }, 600);
    }

    openDrawer() {
      const drawer = document.getElementById('KurashiWishlistDrawer');
      if (drawer) {
        if (!drawer.classList.contains('is-open')) {
          const active = document.activeElement;
          this.returnFocus = active?.closest('#kr-wishlist-toast') ? document.getElementById('kr-wishlist-header-btn') : active;
        }
        drawer.removeAttribute('inert');
        drawer.setAttribute('aria-hidden', 'false');
        document.querySelectorAll('[aria-controls="KurashiWishlistDrawer"]').forEach(btn => btn.setAttribute('aria-expanded', 'true'));
        drawer.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        this.renderDrawer();
        this.focusVisibleDrawer(drawer);
      }
    }

    closeDrawer() {
      this.cancelDrawerFocus();
      const drawer = document.getElementById('KurashiWishlistDrawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
        document.querySelectorAll('[aria-controls="KurashiWishlistDrawer"]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
        this.focusTrigger(this.returnFocus);
        document.body.classList.remove('overflow-hidden');
      }
    }

    renderDrawer() {
      const listContainer = document.getElementById('kr-wishlist-items-container');
      const emptyContainer = document.getElementById('kr-wishlist-empty-container');
      const countSpan = document.getElementById('kr-wishlist-drawer-count');
      if (!listContainer || !emptyContainer) return;

      const active = document.activeElement;
      const activeRow = active?.closest('.kr-wishlist-item');
      const focusedId = activeRow?.dataset.id;
      const focusedClass = activeRow ? active.className : null;
      const oldIndex = activeRow ? [...listContainer.children].indexOf(activeRow) : -1;
      const count = this.items.length;
      emptyContainer.hidden = count > 0;
      if (count > 0) emptyContainer.setAttribute('inert', '');
      else emptyContainer.removeAttribute('inert');
      if (countSpan) countSpan.textContent = `(${count})`;

      if (count === 0) {
        listContainer.innerHTML = '';
        emptyContainer.style.display = 'flex';
      } else {
        emptyContainer.style.display = 'none';
        listContainer.innerHTML = this.items.map(stored => {
          const item = Object.fromEntries(Object.entries(stored).map(([key, value]) => [key, this.escape(value)]));
          // Only storefront-relative links and HTTPS images may enter saved-item markup.
          item.url = /^\/(?!\/)/.test(stored.url || '') ? this.escape(stored.url) : '#';
          item.image = /^(https:\/\/|\/\/)/.test(stored.image || '') ? this.escape(stored.image) : '';
          return `
          <li class="kr-wishlist-item" data-id="${item.id}">
            <a href="${item.url}" class="kr-wishlist-item__image-link">
              ${item.image ? `<img class="kr-wishlist-item__image" src="${item.image}" alt="${item.title}" loading="lazy" width="80" height="106">` : ''}
            </a>
            <div class="kr-wishlist-item__info">
              <a href="${item.url}" class="kr-wishlist-item__title">${item.title}</a>
              <span class="kr-wishlist-item__price">${item.price}</span>
              <div class="kr-wishlist-item__actions">
                <button type="button" class="kr-wishlist-add-cart-btn" onclick="window.KurashiWishlist.addToCart('${item.id}', this)">
                  ${this.escape(this.text('cart'))}
                </button>
                <button type="button" class="kr-wishlist-remove-btn" aria-label="${this.escape(this.text('remove', stored.title))}" onclick="window.KurashiWishlist.removeItem('${item.id}')">
                  ${this.escape(this.text('remove_short'))}
                </button>
              </div>
            </div>
          </li>
        `; }).join('');
      }
      if (focusedId) {
        const rows = [...listContainer.children];
        const row = rows.find(node => node.dataset.id === focusedId) || rows[Math.min(oldIndex, rows.length - 1)];
        const next = row && [...row.querySelectorAll('a, button')].find(node => node.className === focusedClass);
        (next || document.querySelector('.kr-wishlist-drawer__close'))?.focus();
      }
    }

    async addToCart(variantId, btn) {
      if (!variantId) return;
      if (btn) {
        btn.disabled = true;
        btn.textContent = this.text('adding');
      }
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        if (response.ok) {
          this.showToast(this.text('cart_added'));
          this.closeDrawer();
          // Trigger Cart Drawer if present
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
          }
        } else {
          window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        }
      } catch (err) {
        console.error('[Kurashi Wishlist] Error adding to cart', err);
        window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = this.text('cart');
        }
      }
    }

    showToast(message) {
      let toast = document.getElementById('kr-wishlist-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'kr-wishlist-toast';
        toast.className = 'kr-wishlist-toast';
        const status = document.createElement('span');
        status.id = 'kr-wishlist-status';
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        document.body.appendChild(status);
        document.body.appendChild(toast);
      }
      document.getElementById('kr-wishlist-status').textContent = message;
      toast.hidden = false;
      toast.removeAttribute('inert');
      toast.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#c5a059" stroke="#c5a059" stroke-width="1">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
        <span>${this.escape(message)}</span>
        <button type="button" class="kr-wishlist-toast-btn" onclick="window.KurashiWishlist && window.KurashiWishlist.openDrawer()">${this.escape(this.text('view'))}</button>
      `;
      toast.classList.add('is-visible');
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        const hadFocus = toast.contains(document.activeElement);
        toast.classList.remove('is-visible');
        toast.hidden = true;
        toast.setAttribute('inert', '');
        if (hadFocus) this.focusTrigger(document.getElementById('kr-wishlist-header-btn'));
      }, 3500);
    }

    init() {
      document.querySelectorAll('[aria-controls="KurashiWishlistDrawer"]').forEach(btn => { btn.disabled = false; });
      document.addEventListener('keydown', event => {
        const drawer = document.getElementById('KurashiWishlistDrawer');
        if (!drawer?.classList.contains('is-open')) return;
        if (event.key === 'Escape') { event.preventDefault(); this.closeDrawer(); }
        if (event.key === 'Tab') {
          const controls = [...drawer.querySelectorAll('button, a[href]')].filter(el => this.isFocusable(el));
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
        }
      });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.updateUI());
      } else {
        this.updateUI();
      }
    }
  }

  window.KurashiWishlist = new KurashiWishlistManager();
})();
